import { useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';

type SortableItemListDialogProps<T> = {
	open: boolean;
	setOpen: (open: boolean) => void;
	title: string;
	items: T[] | undefined;
	onItemsChange: (items: T[]) => void;
	/** Factory for a new blank item */
	createItem: () => T;
	addLabel?: string;
	emptyMessage?: string;
	/** Render the collapsible content for each item. Use SortableCollapsibleItem for the row wrapper. */
	children: (item: T, index: number, total: number, onChange: (updated: T) => void, onMoveUp: () => void, onMoveDown: () => void, onDelete: () => void) => React.ReactNode;
};

/**
 * Generic dialog that manages a sortable list of items with add / delete / reorder.
 * Delegates rendering of each row to the `children` render prop — wrap each row in SortableCollapsibleItem.
 */
export function SortableItemListDialog<T>({
	open,
	setOpen,
	title,
	items,
	onItemsChange,
	createItem,
	addLabel = 'Add Item',
	emptyMessage,
	children,
}: SortableItemListDialogProps<T>) {
	const [localItems, setLocalItems] = useState<T[]>(items ?? []);

	const handleSave = () => {
		onItemsChange(localItems);
		setOpen(false);
	};

	const handleCancel = () => {
		setOpen(false);
	};

	const handleAdd = () => setLocalItems([...localItems, createItem()]);

	const handleChange = (index: number, updated: T) =>
		setLocalItems(localItems.map((item, i) => (i === index ? updated : item)));

	const handleDelete = (index: number) =>
		setLocalItems(localItems.filter((_, i) => i !== index));

	const handleMoveUp = (index: number) => {
		if (index === 0) return;
		const next = [...localItems];
		[next[index - 1], next[index]] = [next[index], next[index - 1]];
		setLocalItems(next);
	};

	const handleMoveDown = (index: number) => {
		if (index === localItems.length - 1) return;
		const next = [...localItems];
		[next[index], next[index + 1]] = [next[index + 1], next[index]];
		setLocalItems(next);
	};

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<Stack spacing={1} sx={{ mt: 1 }}>
					{localItems.length === 0 && (
						<Typography variant="body2" color="text.secondary">
							{emptyMessage ?? `No items defined. Click "${addLabel}" to get started.`}
						</Typography>
					)}
					{localItems.map((item, index) =>
						children(
							item,
							index,
							localItems.length,
							(updated) => handleChange(index, updated),
							() => handleMoveUp(index),
							() => handleMoveDown(index),
							() => handleDelete(index),
						),
					)}
					<Divider />
					<Button startIcon={<AddCircleIcon />} onClick={handleAdd} variant="outlined" size="small">
						{addLabel}
					</Button>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSave} variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
